/**
 * Table header + body rendering for DocTypeListPageShell.
 * Handles row hierarchy (parent/child), density, resize handles, and empty state.
 *
 * Extracted from DocTypeListPageShell.tsx for maintainability.
 */

import { Fragment, useRef, useState } from "react";
import type { Table as TanstackTable } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { captureListRowClicked } from "@/lib/analytics/posthog-events";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { TableDensity, TableTextSize } from "@/lib/store/preferencesStore";
import type { InlineEditingContext, InlineCellRangePos } from "@/components/inline-edit/types";
import { InlineEditableCell } from "@/components/inline-edit/InlineEditableCell";

// Inset box-shadow for range perimeter (Google Sheets style)
const RANGE_COLOR = '#2563eb'; // blue-600
function buildInlineRangeShadow(pos: InlineCellRangePos): string {
  const parts: string[] = [];
  if (pos.top) parts.push(`inset 0 2px 0 0 ${RANGE_COLOR}`);
  if (pos.bottom) parts.push(`inset 0 -2px 0 0 ${RANGE_COLOR}`);
  if (pos.left) parts.push(`inset 2px 0 0 0 ${RANGE_COLOR}`);
  if (pos.right) parts.push(`inset -2px 0 0 0 ${RANGE_COLOR}`);
  return parts.join(', ');
}

/** Column width info passed to renderSubRow so child content can align with the parent grid */
export interface SubRowColumnInfo {
  /** Pixel width of each visible column, in order */
  columnWidths: number[];
  /** Total column count */
  columnCount: number;
}

export type ListTableBodyProps<T> = {
  table: TanstackTable<T>;
  tableDensity: TableDensity;
  tableTextSize: TableTextSize;
  onRowClick?: (row: T) => void;
  tableContainerClassName?: string;
  translations: Record<string, string>;
  /** Optional: render an inline sub-row below each row. Return null to skip. */
  renderSubRow?: (row: T, columns: SubRowColumnInfo) => React.ReactNode | null;
  /** ID of the row whose sub-row is currently open (for highlight styling) */
  activeSubRowId?: string;
  /** Row ID getter — needed for activeSubRowId comparison */
  getRowId?: (row: T) => string;
  /** Inline editing context — when provided, editable cells become click-to-edit */
  inlineEditing?: InlineEditingContext;
};

export function ListTableBody<T>({
  table,
  tableDensity,
  tableTextSize,
  onRowClick,
  tableContainerClassName,
  translations,
  renderSubRow,
  activeSubRowId,
  getRowId = (row: T) => (row as Record<string, unknown>).name as string ?? String(row),
  inlineEditing,
}: ListTableBodyProps<T>) {
  // Compute column widths once for sub-row alignment
  const subRowColumns: SubRowColumnInfo | undefined = renderSubRow
    ? {
        columnWidths: table.getVisibleLeafColumns().map((c) => c.getSize()),
        columnCount: table.getVisibleLeafColumns().length,
      }
    : undefined;

  // Reorderable data columns via native drag-and-drop (id not __-prefixed).
  const [dragCol, setDragCol] = useState<string | null>(null);
  // Set when the pointer grabs a resize handle, so we don't start a column drag.
  const suppressDrag = useRef(false);
  const canReorder = (id: string) => !id.startsWith("__");
  const reorder = (fromId: string, toId: string) => {
    if (fromId === toId || !canReorder(fromId) || !canReorder(toId)) return;
    const order = table.getVisibleLeafColumns().map((c) => c.id);
    const from = order.indexOf(fromId);
    const to = order.indexOf(toId);
    if (from < 0 || to < 0) return;
    order.splice(to, 0, order.splice(from, 1)[0]);
    table.setColumnOrder(order);
  };

  return (
    <div
      className={cn("group/density", tableContainerClassName ?? "overflow-x-auto overflow-y-hidden rounded-xl border border-border bg-card/50 backdrop-blur-sm shadow-sm")}
      data-density={tableDensity}
      data-textsize={tableTextSize}
    >
      <Table className="min-w-full table-fixed" style={{ width: '100%' }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const hMeta = header.column.columnDef.meta as Record<string, unknown> | undefined;
                const hNoPad = hMeta?.noPadding === true;
                const isAddCol = header.column.id === '__add_column';
                const draggable = canReorder(header.column.id);
                return (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  className={cn(
                    "relative group/th",
                    hNoPad && "!px-0",
                    draggable && "cursor-grab",
                    dragCol === header.column.id && "opacity-40",
                  )}
                  style={isAddCol ? undefined : { width: header.getSize() }}
                  draggable={draggable}
                  onDragStart={draggable ? (e: React.DragEvent) => {
                    if (suppressDrag.current) { e.preventDefault(); suppressDrag.current = false; return; }
                    setDragCol(header.column.id);
                    e.dataTransfer.effectAllowed = "move";
                  } : undefined}
                  onDragOver={draggable && dragCol ? (e: React.DragEvent) => e.preventDefault() : undefined}
                  onDrop={draggable && dragCol ? (e: React.DragEvent) => {
                    e.preventDefault();
                    reorder(dragCol, header.column.id);
                    setDragCol(null);
                  } : undefined}
                  onDragEnd={() => setDragCol(null)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  {header.column.getCanResize() && (
                    <div
                      onDoubleClick={() => header.column.resetSize()}
                      onMouseDown={(e) => { suppressDrag.current = true; header.getResizeHandler()(e); }}
                      onTouchStart={header.getResizeHandler()}
                      className={cn(
                        "absolute right-0 top-0 h-full w-[5px] -mr-[2px] z-20 cursor-col-resize select-none touch-none",
                        header.column.getIsResizing() ? "bg-blue-400" : "bg-transparent hover:bg-neutral-300 dark:hover:bg-neutral-600",
                      )}
                    />
                  )}
                </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, rowIdx, allRows) => {
              const depth = row.depth || 0;
              const isChild = depth > 0;
              const isParentExpanded = !isChild && row.getIsExpanded();
              // Check if this is the last child in a group
              const nextRow = allRows[rowIdx + 1];
              const isLastChild = isChild && (!nextRow || (nextRow.depth || 0) === 0);

              const isSubRowActive = activeSubRowId != null && getRowId(row.original) === activeSubRowId;

              // Archived row detection: check both "disabled" and "disable" field names
              const rowData = row.original as Record<string, unknown>;
              const isArchived = rowData.disabled === 1 || rowData.disable === 1;

              return (
              <Fragment key={row.id}>
              <TableRow
                data-state={row.getIsSelected() && "selected"}
                data-row-id={getRowId(row.original)}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e: React.KeyboardEvent) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onRowClick(row.original);
                  }
                } : undefined}
                className={cn(
                  "group",
                  onRowClick && "cursor-pointer hover:bg-accent/50 transition-colors",
                  // Parent row with expanded children: no bottom border (children flow into it)
                  isParentExpanded && "border-b-0",
                  // Child rows: tinted background, no row border (tree connector handles visual separation)
                  isChild && "bg-neutral-50/80 dark:bg-neutral-900/40 border-b-0",
                  // Last child: thicker border to close the group
                  isLastChild && "!border-b-2 border-b-neutral-200 dark:border-b-neutral-700",
                  // Highlight row when its sub-row (detail panel) is open
                  isSubRowActive && "bg-sky-50/40 dark:bg-sky-950/20",
                  // Archived rows: dimmed per UX spec
                  isArchived && "opacity-60",
                )}
                onClick={() => {
                  if (onRowClick) {
                    const r = row.original as Record<string, unknown>;
                    captureListRowClicked((r.name as string) || '');
                    onRowClick(row.original);
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  // Adjust padding for system columns spacing
                  const isAvatarColumn = cell.column.id === "__assigned_to";
                  const isTimeColumn = cell.column.id === "__created";
                  const isCommentColumn = cell.column.id === "__comments";

                  const meta = cell.column.columnDef.meta as Record<string, unknown> | undefined;
                  const noPad = meta?.noPadding === true;

                  let cellClassName = noPad ? "!px-0 !py-0" : "";
                  if (isAvatarColumn) {
                    cellClassName = "pr-2";
                  } else if (isTimeColumn) {
                    cellClassName = "pl-0 pr-0";
                  } else if (isCommentColumn) {
                    cellClassName = "pl-0 pr-0";
                  }

                  const isCellAddCol = cell.column.id === '__add_column';
                  const isExpandCell = cell.column.id === '__expand';

                  // Inline editing: wrap editable cells (G17: skip __-prefixed system columns)
                  const cellFieldname = cell.column.id;
                  const isSystemCol = cellFieldname.startsWith('__');
                  const isInlineEditable = !isSystemCol && !!inlineEditing?.editableFields.has(cellFieldname);

                  // Google Sheets style: blue outline on the <td> when this cell is active
                  const isActiveCell = isInlineEditable
                    && inlineEditing?.activeCell?.rowName === getRowId(row.original)
                    && inlineEditing?.activeCell?.fieldname === cellFieldname;

                  // Range selection highlight
                  const inRange = !isSystemCol && !!inlineEditing?.isCellInRange(rowIdx, cellFieldname);
                  const rangePos = inRange ? inlineEditing?.getCellRangePos(rowIdx, cellFieldname) : null;
                  const rangeShadow = rangePos && !isActiveCell ? buildInlineRangeShadow(rangePos) : undefined;

                  // Fill handle: show on active cell (no range) or bottom-right of range
                  const showFillHandle = isInlineEditable && inlineEditing && (
                    (isActiveCell && !inlineEditing.rangeRect) ||
                    (rangePos?.bottom && rangePos?.right)
                  );

                  const cellContent = flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  );

                  return (
                    <TableCell
                      key={cell.id}
                      data-col-id={isSystemCol ? undefined : cellFieldname}
                      className={cn(
                        cellClassName,
                        isActiveCell && 'shadow-[inset_0_0_0_2px_#2563eb] z-10 relative',
                        inRange && !isActiveCell && 'bg-blue-500/[0.08]',
                        (showFillHandle || (rangePos && !isActiveCell)) && 'relative',
                        // Fill handle is fully inside cell — no overflow override needed
                      )}
                      style={{
                        ...(isCellAddCol ? {} : { width: cell.column.getSize() }),
                        ...(isExpandCell && (isChild || isParentExpanded) ? { height: 0 } : {}),
                        ...(rangeShadow ? { boxShadow: rangeShadow } : undefined),
                      }}
                      onMouseDown={isInlineEditable && inlineEditing ? (e) => {
                        inlineEditing.handleCellMouseDown(rowIdx, cellFieldname, getRowId(row.original), e);
                      } : undefined}
                    >
                      {isInlineEditable && inlineEditing ? (
                        <InlineEditableCell
                          rowName={getRowId(row.original)}
                          fieldname={cellFieldname}
                          value={(row.original as Record<string, unknown>)[cellFieldname]}
                          context={inlineEditing}
                        >
                          {cellContent}
                        </InlineEditableCell>
                      ) : cellContent}
                      {showFillHandle && inlineEditing && (
                        <div
                          className="absolute bottom-0 right-0 w-[16px] h-[16px] cursor-crosshair z-30 flex items-end justify-end touch-none select-none"
                          onMouseDown={inlineEditing.handleFillMouseDown}
                        >
                          <div className="w-[8px] h-[8px] bg-blue-600 border-2 border-white rounded-[1px] shrink-0 pointer-events-none" />
                        </div>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
              {renderSubRow && subRowColumns && (() => {
                const subRowContent = renderSubRow(row.original, subRowColumns);
                if (!subRowContent) return null;
                return subRowContent;
              })()}
              </Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center"
              >
                {translations.noResults || "No results"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
